import { useSyncExternalStore } from "react";

const USES_KEY = "theta:submit-shortcut-uses";
/** Stop showing the inline hint once someone has used the shortcut this many times. */
const LEARNED_AFTER = 3;

// A tiny external store so every mounted hint retires at the same moment — the
// counter is written by RichTextInput, but read by hints rendered elsewhere.
let uses = -1; // -1 = not yet read from localStorage
const listeners = new Set<() => void>();

function readUses(): number {
  if (uses < 0) {
    try {
      uses = Number(localStorage.getItem(USES_KEY)) || 0;
    } catch {
      // localStorage unavailable (privacy mode) — treat as never used.
      uses = 0;
    }
  }
  return uses;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Record that a submit came from Cmd/Ctrl+Enter rather than the button. */
export function recordSubmitShortcutUse(): void {
  uses = readUses() + 1;
  try {
    localStorage.setItem(USES_KEY, String(uses));
  } catch {
    // Ignore write failures (quota, privacy mode).
  }
  listeners.forEach((l) => l());
}

// `useSyncExternalStore` with a never-firing subscription is the hydration-safe
// way to ask "are we past the server render yet?" — React uses the server
// snapshot for the hydrating render, then swaps in the client one.
const neverChanges = () => () => {};
const onClient = () => true;
const onServer = () => false;
const learnedOnServer = () => LEARNED_AFTER;

function platformModKey(): string | null {
  // A touch-only device has no Cmd/Ctrl key — the hint would be pure noise.
  // (jsdom has no matchMedia; assume a keyboard when we can't tell.)
  if (
    typeof window.matchMedia === "function" &&
    !window.matchMedia("(hover: hover) and (pointer: fine)").matches
  ) {
    return null;
  }
  return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent) ? "⌘" : "Ctrl";
}

/**
 * The platform label for the submit shortcut's modifier, plus whether the
 * teaching hint is still worth showing.
 *
 * `modKey` is null during server render and hydration, and on touch-only
 * devices where there is no modifier key to press.
 */
export function useSubmitShortcut(): { modKey: string | null; showHint: boolean } {
  const mounted = useSyncExternalStore(neverChanges, onClient, onServer);
  const timesUsed = useSyncExternalStore(subscribe, readUses, learnedOnServer);

  const modKey = mounted ? platformModKey() : null;
  return { modKey, showHint: modKey !== null && timesUsed < LEARNED_AFTER };
}
